import React, { useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'

interface FastAvatarProps {
  src?: string
  name?: string
  size?: string
  /** Fixed pixel size (e.g. "32px") or a Chakra responsive object. */
  boxSize?: any
  fallbackSrc?: string
  [key: string]: any
}

/**
 * Avatar with native lazy loading (loading="lazy").
 *
 * The image element is rendered immediately and the browser handles deferral
 * and caching. We deliberately avoid JS-based IntersectionObserver gating and
 * per-mount opacity/skeleton transitions: those re-run on every mount/refetch
 * (e.g. React Query refocus, pagination, tab switches) and make cached avatars
 * flicker. Same rationale as LazyLoadImg. Falls back to initials on missing
 * src or load error.
 */
const FastAvatar: React.FC<FastAvatarProps> = ({
  src,
  name,
  size = 'md',
  boxSize,
  fallbackSrc,
  ...props
}) => {
  const [imageError, setImageError] = useState(false)

  // Size mapping
  const sizeMap = {
    'xs': '24px',
    'sm': '32px',
    'md': '40px',
    'lg': '56px',
    'xl': '72px',
    '2xl': '96px'
  }

  const avatarSize = boxSize || sizeMap[size as keyof typeof sizeMap] || size
  const resolvedSrc = imageError ? fallbackSrc : src

  if (!resolvedSrc) {
    const initials = name
      ? name.split(' ').map((n) => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
      : ''
    // avatarSize may be a responsive object; parseInt then yields NaN and we fall back to the largest size.
    const sizePx = parseInt(avatarSize as string)
    const fontSize = sizePx <= 32 ? '11px' : sizePx <= 40 ? '13px' : '15px'
    return (
      <Flex
        width={avatarSize}
        height={avatarSize}
        minW={avatarSize}
        borderRadius="full"
        bg="solid.50"
        align="center"
        justify="center"
        {...props}
      >
        {initials && (
          <Text fontSize={fontSize} fontWeight="600" color="solid.400" lineHeight="1" userSelect="none">
            {initials}
          </Text>
        )}
      </Flex>
    )
  }

  return (
    <Box
      position="relative"
      width={avatarSize}
      height={avatarSize}
      minW={avatarSize}
      borderRadius="full"
      overflow="hidden"
      bg="solid.50"
      {...props}
    >
      <img
        src={resolvedSrc}
        alt={name || 'Profile picture'}
        onError={() => setImageError(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: '50%',
          display: 'block'
        }}
        loading="lazy"
        decoding="async"
      />
    </Box>
  )
}

export default FastAvatar
